import { Request, Response } from "express";
import mongoose from "mongoose";

import { FEEDBACK_STATUS, HelpFeedback, Student, Teacher } from "../models";
import { logger } from "../utils/logger";

// Standard API response interface
interface APIResponse<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

// Extended Request interface for authenticated users
interface AuthRequest extends Request {
  user?: {
    userId: string;
    role: string;
    schoolId?: string;
  };
}

class HelpFeedbackController {
  /**
   * Submit help request or feedback
   * @route POST /api/v1/help-feedback
   * @access Private
   */
  async submitHelpFeedback(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const role = req.user?.role;
      const {
        type,
        category,
        feedbackType,
        subject,
        description,
        priority,
        attachments,
      } = req.body;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      if (!type || !subject || !description) {
        res.status(400).json({
          success: false,
          message: "Missing required fields: type, subject, description",
        } as APIResponse);
        return;
      }

      let schoolId: mongoose.Types.ObjectId | undefined;

      switch (role) {
        case "student": {
          const student = await Student.findOne({
            userId: new mongoose.Types.ObjectId(userId),
          });
          if (student) {
            schoolId = student.schoolId;
          }

          break;
        }
        case "teacher": {
          const teacher = await Teacher.findOne({
            userId: new mongoose.Types.ObjectId(userId),
          });
          if (teacher) {
            schoolId = teacher.schoolId;
          }

          break;
        }
        case "school": {
          schoolId = new mongoose.Types.ObjectId(req.user?.schoolId);

          break;
        }
        // No default
      }

      const helpFeedback = new HelpFeedback({
        userId: new mongoose.Types.ObjectId(userId),
        schoolId,
        type,
        category,
        feedbackType: type === "feedback" ? feedbackType : undefined,
        subject,
        description,
        priority: priority || "medium",
        attachments: attachments || [],
        status: FEEDBACK_STATUS.PENDING,
      });

      await helpFeedback.save();

      logger.info(`Help/Feedback submitted: ${helpFeedback.id}`, {
        helpFeedbackId: helpFeedback.id,
        userId,
        type,
      });

      res.status(201).json({
        success: true,
        message: `${type === "help" ? "Help request" : "Feedback"} submitted successfully`,
        data: {
          helpFeedback: {
            id: helpFeedback.id,
            type: helpFeedback.type,
            category: helpFeedback.category,
            subject: helpFeedback.subject,
            status: helpFeedback.status,
            priority: helpFeedback.priority,
            createdAt: helpFeedback.createdAt,
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Submit help/feedback error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to submit help/feedback",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get all help/feedback for authenticated user
   * @route GET /api/v1/help-feedback
   * @access Private
   */
  async getHelpFeedback(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { type, status, page = 1, limit = 20 } = req.query;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      const filterQuery: any = { userId: new mongoose.Types.ObjectId(userId) };

      if (type) filterQuery.type = type;
      if (status) filterQuery.status = status;

      const pageNum = Number.parseInt(page as string, 10);
      const limitNum = Number.parseInt(limit as string, 10);
      const skip = (pageNum - 1) * limitNum;

      // eslint-disable-next-line unicorn/no-array-callback-reference
      const helpFeedback = await HelpFeedback.find(filterQuery)
        .populate("respondedBy", "email")
        .sort({ createdAt: -1 })
        .skip(skip)
        .limit(limitNum);

      const total = await HelpFeedback.countDocuments(filterQuery);

      res.status(200).json({
        success: true,
        message: "Help/Feedback retrieved successfully",
        data: {
          helpFeedback: helpFeedback.map(hf => ({
            id: hf.id,
            type: hf.type,
            category: hf.category,
            feedbackType: hf.feedbackType,
            subject: hf.subject,
            description: hf.description,
            status: hf.status,
            priority: hf.priority,
            response: hf.response,
            respondedBy: hf.respondedBy
              ? {
                  id: (hf.respondedBy as any).id,
                  email: (hf.respondedBy as any).email,
                }
              : null,
            respondedAt: hf.respondedAt,
            attachments: hf.attachments,
            createdAt: hf.createdAt,
            updatedAt: hf.updatedAt,
          })),
          pagination: {
            page: pageNum,
            limit: limitNum,
            total,
            pages: Math.ceil(total / limitNum),
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Get help/feedback error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve help/feedback",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get all help/feedback for school panel
   * @route GET /api/v1/school/help-feedback
   * @access Private (School Admin)
   */
  async getSchoolHelpFeedback(req: AuthRequest, res: Response): Promise<void> {
    try {
      const schoolId = req.user?.schoolId;
      const { type, status, priority, page = 1, limit = 20 } = req.query;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
        } as APIResponse);
        return;
      }

      const filterQuery: any = {
        schoolId: new mongoose.Types.ObjectId(schoolId),
      };

      if (type) filterQuery.type = type;
      if (status) filterQuery.status = status;
      if (priority) filterQuery.priority = priority;

      const pageNum = Number.parseInt(page as string, 10);
      const limitNum = Number.parseInt(limit as string, 10);
      const skip = (pageNum - 1) * limitNum;

      // eslint-disable-next-line unicorn/no-array-callback-reference
      const helpFeedback = await HelpFeedback.find(filterQuery)
        .populate("userId", "email role")
        .populate("respondedBy", "email")
        .sort({ createdAt: -1 })
        .skip(skip)
        .limit(limitNum);

      const total = await HelpFeedback.countDocuments(filterQuery);

      res.status(200).json({
        success: true,
        message: "Help/Feedback retrieved successfully",
        data: {
          helpFeedback: helpFeedback.map(hf => ({
            id: hf.id,
            type: hf.type,
            category: hf.category,
            feedbackType: hf.feedbackType,
            subject: hf.subject,
            description: hf.description,
            status: hf.status,
            priority: hf.priority,
            user: {
              id: (hf.userId as any).id,
              email: (hf.userId as any).email,
              role: (hf.userId as any).role,
            },
            response: hf.response,
            respondedBy: hf.respondedBy
              ? {
                  id: (hf.respondedBy as any).id,
                  email: (hf.respondedBy as any).email,
                }
              : null,
            respondedAt: hf.respondedAt,
            attachments: hf.attachments,
            createdAt: hf.createdAt,
            updatedAt: hf.updatedAt,
          })),
          pagination: {
            page: pageNum,
            limit: limitNum,
            total,
            pages: Math.ceil(total / limitNum),
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Get school help/feedback error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve help/feedback",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Respond to help/feedback
   * @route PUT /api/v1/school/help-feedback/:id/respond
   * @access Private (School Admin)
   */
  async respondToHelpFeedback(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;
      const { response } = req.body;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid help/feedback ID",
        } as APIResponse);
        return;
      }

      if (!userId || !schoolId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      if (!response) {
        res.status(400).json({
          success: false,
          message: "Response is required",
        } as APIResponse);
        return;
      }

      const helpFeedback = await HelpFeedback.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!helpFeedback) {
        res.status(404).json({
          success: false,
          message: "Help/Feedback not found",
        } as APIResponse);
        return;
      }

      await helpFeedback.respond(new mongoose.Types.ObjectId(userId), response);

      logger.info(`Help/Feedback responded: ${helpFeedback.id}`, {
        helpFeedbackId: helpFeedback.id,
        respondedBy: userId,
      });

      res.status(200).json({
        success: true,
        message: "Response submitted successfully",
        data: {
          helpFeedback: {
            id: helpFeedback.id,
            status: helpFeedback.status,
            response: helpFeedback.response,
            respondedAt: helpFeedback.respondedAt,
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Respond to help/feedback error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to respond to help/feedback",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }
}

export const helpFeedbackController = new HelpFeedbackController();
